import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  Label
} from "recharts";
import { Box, Card, CardContent, Typography, Paper, Divider } from "@mui/material";
import dayjs from "dayjs";
import { formatKpiValue, getKpiConfig } from "../../../../shared/utils/prediction-utils";

const CustomTooltip = ({ active, payload, label, kpi }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const isPrediction = data.is_prediction;
    const confidence = data.confidence_pct;

    const formattedLabel = dayjs(label).isValid() 
      ? dayjs(label).format("MMM D, YYYY") 
      : label;

    return (
      <Paper elevation={3} sx={{ p: 1.5, border: "1px solid #e0e0e0", fontSize: "0.75rem", minWidth: 200, zIndex: 100 }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            mb: 0.5, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 2 
          }}
        >
            <span style={{ fontWeight: 'bold' }}>{formattedLabel}</span>
            <Box 
              component="span" 
              sx={{ 
                fontSize: '0.65rem', 
                px: 0.8, 
                py: 0.2, 
                borderRadius: 1, 
                whiteSpace: 'nowrap',
                bgcolor: isPrediction ? '#e3f2fd' : '#f5f5f5',
                color: isPrediction ? '#1976d2' : '#666',
                fontWeight: 'bold'
              }}
            >
                {isPrediction ? "FORECAST" : "ACTUAL"}
            </Box>
        </Typography>

        <Divider sx={{ my: 1 }} />
        
        <Typography variant="body2" color="primary" fontWeight="bold">
          Value: {formatKpiValue(data.value, kpi)}
        </Typography>

        {isPrediction && confidence && (
          <Typography variant="caption" sx={{ color: '#1976d2', display: 'block', mt: 0.5, fontWeight: 600 }}>
            Confidence Score: {confidence}%
          </Typography>
        )}
      </Paper>
    );
  }
  return null;
};

export const PredictionBarChart = ({ data, kpi }: { data: any[]; kpi: string }) => {
  if (!data || data.length === 0) return null;

  const config = getKpiConfig(kpi);
  const xAxisKey = "date" in data[0] ? "date" : "month";
  
  const firstPrediction = data.find(item => item.is_prediction);
  const predictionStartDate = firstPrediction ? firstPrediction[xAxisKey] : null;

  const actualColor = "#1976d2";   
  const forecastColor = "#90caf9"; 
  const strokeColor = "#64b5f6";   

  return (
    <Card sx={{ borderRadius: "16px", boxShadow: 2, width: "100%", overflow: 'visible' }}>
      <CardContent sx={{ pb: "16px !important" }}>
        
        {/* Header & Legend */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: '1rem' }}>
                {config.label} {config.unit ? `(${config.unit})` : ''}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: actualColor }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>Actual</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: `1px dashed ${strokeColor}`, bgcolor: 'rgba(144, 202, 249, 0.3)' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>Forecast</Typography>
                </Box>
            </Box>
        </Box>

        {/* Chart Area */}
        <Box sx={{ width: "100%", height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
            <BarChart 
                data={data} 
                margin={{ top: 25, right: 10, left: 0, bottom: 5 }} 
                barGap={2}
            >
                <defs>
                    <linearGradient id={`actualGradient-${kpi}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={actualColor} stopOpacity={0.8}/>
                        <stop offset="100%" stopColor={actualColor} stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id={`forecastGradient-${kpi}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={forecastColor} stopOpacity={0.6}/>
                        <stop offset="100%" stopColor={forecastColor} stopOpacity={0.1}/>
                    </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                
                <XAxis 
                    dataKey={xAxisKey} 
                    tickFormatter={(val) => {
                        if (xAxisKey === 'date') return dayjs(val).format("ddd"); 
                        return dayjs(val).format("MMM");
                    }}
                    style={{ fontSize: "11px", fill: '#9e9e9e' }} 
                    axisLine={false}
                    tickLine={false}
                    dy={10}
                />
                
                <YAxis 
                    style={{ fontSize: "11px", fill: '#9e9e9e' }} 
                    axisLine={false}
                    tickLine={false}
                    tickCount={5}
                />
                
                <Tooltip content={<CustomTooltip kpi={kpi} />} cursor={{ fill: 'rgba(0,0,0,0.02)' }} />
                
                {predictionStartDate && (
                    <ReferenceLine x={predictionStartDate} stroke="transparent">
                        <Label 
                            content={({ viewBox }: any) => {
                                const { x, y } = viewBox;
                                const shiftLeft = 40; 
                                const finalX = x - shiftLeft; 
                                const pillWidth = 60;
                                const pillHeight = 18;
                                const lineLength = 250;

                                return (
                                    <g style={{ pointerEvents: 'none' }}>
                                            <line 
                                                x1={finalX} y1={y} 
                                                x2={finalX} y2={y + lineLength} 
                                                stroke={strokeColor} 
                                                strokeDasharray="3 3" 
                                                strokeWidth={1}
                                            />
                                            <rect 
                                                x={finalX - (pillWidth / 2)} 
                                                y={y - pillHeight - 5} 
                                                width={pillWidth} 
                                                height={pillHeight} 
                                                rx={11} 
                                                fill="#d1e9fc" 
                                            />
                                            <text 
                                                x={finalX} 
                                                y={y - 14} 
                                                fill="#1976d2" 
                                                fontSize={10} 
                                                fontWeight="bold" 
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                            >
                                                Forecast
                                            </text>
                                    </g>
                                )
                            }}
                        />
                    </ReferenceLine>
                )}

                <Bar 
                    dataKey="value" 
                    radius={[20, 20, 20, 20]} 
                    maxBarSize={30}
                >
                    {data.map((entry, index) => {
                        const isForecast = entry.is_prediction;
                        return (
                            <Cell 
                                key={`cell-${index}`} 
                                fill={isForecast ? `url(#forecastGradient-${kpi})` : `url(#actualGradient-${kpi})`}
                                stroke={isForecast ? actualColor : 'none'} 
                                strokeWidth={isForecast ? 1 : 0}
                                strokeDasharray={isForecast ? "4 2" : "0 0"} 
                            />
                        );
                    })}
                </Bar>
            </BarChart>
            </ResponsiveContainer>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, px: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>↑</span> {config.axisLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>→</span> Duration
            </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};